// src/oura/newscan/tellOura.ts
//
// "Tell Oura" on a First AI check's Review (Jack, 19 Oct 2026: "like Claude,
// where if i reply something it will affect the other stuff in the bottom").
// Staff say what is wrong in their own words, and Oura answers with the
// changes it would make, each in plain words with what it touches below.
// Nothing moves until staff press Apply, and one Undo takes it all back.
//
// The reading here is a stand-in for the model the backend will run (BE
// handoff): it knows three things on the Review today, the branches, a form
// goal that asks to change the core service, and the plan's priority
// keywords. Every change it proposes is one of the cards' own options, so the
// backend's version can return the same shapes. Pure.

import type { DecisionChoice, ScanDraft, ScanDraftDecision } from "@service/aeo/scanDraftTypes";
import { activeBranches, branchRows, openBranchCards, readBranches } from "./branches";
import { plural } from "./copy";

/** One change Oura proposes. `touches`: the cards below it changes, for the highlight. */
export type TellChange =
    | { id: "branches"; kind: "branches"; keep: string[]; drop: string[]; text: string; effect: string; touches: string[] }
    | { id: string; kind: "answer"; decisionId: string; choice: DecisionChoice; text: string; effect: string; touches: string[]; after?: boolean }
    | { id: string; kind: "plan"; decisionId: string; values: string[]; text: string; effect: string; touches: string[]; after?: boolean };

export interface TellReading {
    changes: TellChange[];
    /** Oura's line above the changes, or why it changed nothing. */
    reply: string;
    /** Sentences staff can tap when Oura did not follow. */
    suggestions: string[];
}

const norm = (s: string) => ` ${s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim()} `;
const has = (t: string, phrase: string) => t.includes(norm(phrase));
const open = (draft: ScanDraft, pred: (d: ScanDraftDecision) => boolean) => draft.decisions.find((d) => d.state === "open" && pred(d)) ?? null;

/** The words of a service that carry its meaning ("pigmentation treatment" gives "pigmentation"). */
const GENERIC = new Set(["treatment", "treatments", "service", "services", "removal", "the", "and", "of"]);
const keyWords = (s: string) => norm(s).trim().split(" ").filter((w) => w.length > 2 && !GENERIC.has(w));

/** The sentences Oura offers when staff have not typed yet, or when it did not follow. */
export function tellSuggestions(draft: ScanDraft): string[] {
    const out: string[] = [];
    const list = branchRows(draft);
    const named = list.rows.find((r) => r.doubt?.startsWith("Part of"));
    if (named) out.push(`${named.name} is part of our name`);
    const goal = open(draft, (d) => d.type === "form_goal_core");
    const svc = goal ? coreService(goal) : null;
    if (svc) out.push(`Make ${svc} a core service`);
    if (open(draft, (d) => d.type === "plan_keywords" && Boolean(d.recommended))) out.push("The keywords look right");
    return out;
}

function coreService(d: ScanDraftDecision): string | null {
    const a = d.alternatives.find((o) => o.action.kind === "set_core_service")?.action as { kind: string; name?: string } | undefined;
    return a?.name ?? null;
}

/** What the sentence asks for, as the cards' own options. */
export function readTell(draft: ScanDraft, text: string): TellReading {
    const t = norm(text);
    const changes: TellChange[] = [];

    // 1. The form goal: "make pigmentation a core service", "keep acne scars as the core".
    const goal = open(draft, (d) => d.type === "form_goal_core");
    const svc = goal ? coreService(goal) : null;
    if (goal && svc) {
        const keepIdx = goal.alternatives.findIndex((o) => o.action.kind === "dismiss");
        const keepLabel = keepIdx >= 0 ? goal.alternatives[keepIdx].label : "";
        const current = keepLabel.replace(/^Keep\s+/i, "");
        const aboutSvc = keyWords(svc).some((w) => has(t, w));
        const aboutCurrent = current && keyWords(current).some((w) => has(t, w));
        const no = /\b(?:not|don t|dont|no|keep)\b/.test(t);
        const core = /\b(?:core|main|focus|priority|push|lead)\b/.test(t);
        const n = Number(/change (\d+)/.exec(goal.detail ?? "")?.[1] ?? 0);
        if (aboutSvc && core && !(no && !aboutCurrent && / (?:not|don t|dont) /.test(t))) {
            const i = goal.alternatives.findIndex((o) => o.action.kind === "set_core_service");
            changes.push({
                id: goal.id,
                kind: "answer",
                decisionId: goal.id,
                choice: { alternative: i },
                text: `Make ${svc} the core service`,
                effect: n ? `Rewrites ${plural(n, "question")} to ask about ${svc} in place of ${current || "the current core"}` : `The core service questions ask about ${svc}`,
                touches: [goal.id],
            });
        } else if (keepIdx >= 0 && ((aboutCurrent && /\bkeep\b/.test(t)) || (aboutSvc && / (?:not|don t|dont) (?:make |a )?(?:core|main)/.test(t)))) {
            changes.push({ id: goal.id, kind: "answer", decisionId: goal.id, choice: { alternative: keepIdx }, text: keepLabel, effect: "The questions stay as they are", touches: [goal.id] });
        }
    }

    // 2. The branches: "Lush is part of our name", "the branches are Mont Kiara and Bukit Bintang".
    const list = branchRows(draft);
    const names = list.rows.map((r) => r.name);
    const guess = list.rows.filter((r) => r.guess).map((r) => r.name);
    const reading = names.length ? readBranches(text, names, guess) : null;
    if (reading) {
        const active = activeBranches(draft).map((o) => o.name.toLowerCase());
        const same = reading.keep.length === active.length && reading.keep.every((k) => active.includes(k.toLowerCase()));
        const cardsOpen = openBranchCards(draft).length > 0;
        if (!same || cardsOpen) {
            const dropped = reading.drop.filter((n) => active.includes(n.toLowerCase()));
            // Cards that go away with a dropped name: "Question 21 contains the business name (lush)".
            const gone = draft.decisions.filter((d) => d.state === "open" && dropped.some((n) => d.id.toLowerCase().endsWith(`@${n.toLowerCase()}`)));
            const questions = new Set(gone.flatMap((d) => d.slots));
            const k = reading.keep.length;
            const effects = [
                questions.size ? `Clears ${plural(questions.size, "question")} that used ${dropped.filter((n) => gone.some((d) => d.id.toLowerCase().endsWith(`@${n.toLowerCase()}`))).join(" and ")} as a place` : null,
                k === 2 ? "Splits the 26 location questions 14 and 12" : k > 1 ? `Splits the 26 location questions across ${k}` : null,
                list.quote !== null ? (k === list.quote ? `Matches the quote (${list.quote})` : `The quote paid for ${list.quote}`) : null,
            ].filter(Boolean);
            changes.push({
                id: "branches",
                kind: "branches",
                keep: reading.keep,
                drop: reading.drop,
                text: `Branches: ${reading.keep.join(" and ")}${reading.drop.length ? `. Not branches: ${reading.drop.join(" and ")}` : ""}`,
                effect: effects.join(". "),
                touches: ["branches", ...gone.map((d) => d.id)],
            });
        }
    }

    // 3. The plan's keywords: "the keywords look right", "drop facials from the keywords", "add chemical peel".
    const kw = open(draft, (d) => d.type === "plan_keywords");
    if (kw && /\bkeywords?\b/.test(t)) {
        const picks = kw.picks ?? [];
        const removeM = /\b(?:drop|remove|take out|without)\s+(.+?)(?:\s+from\b|$)/.exec(t.trim());
        const addM = /\badd\s+(.+?)(?:\s+to\b|$)/.exec(t.trim());
        const removed = removeM ? picks.filter((p) => norm(removeM[1]).includes(norm(p)) || norm(p).includes(norm(removeM[1]))) : [];
        const added = addM ? addM[1].trim() : "";
        if (removed.length || added) {
            const values = [...picks.filter((p) => !removed.includes(p)), ...(added ? [added] : [])];
            changes.push({
                id: kw.id,
                kind: "plan",
                decisionId: kw.id,
                values,
                text: [removed.length ? `Drop ${removed.join(" and ")} from the keywords` : null, added ? `Add “${added}” to the keywords` : null].filter(Boolean).join(", "),
                effect: `Saves ${plural(values.length, "priority keyword")} for the plan`,
                touches: [kw.id],
                after: true,
            });
        } else if (kw.recommended && /\b(?:right|fine|good|ok|okay|correct|use|keep|look)\b/.test(t)) {
            changes.push({
                id: kw.id,
                kind: "answer",
                decisionId: kw.id,
                choice: "recommended",
                text: `Use the ${plural(picks.length, "keyword")} Oura picked`,
                effect: `Saves them as the plan's priority keywords`,
                touches: [kw.id],
                after: true,
            });
        }
    }

    if (changes.length) {
        return { changes, reply: changes.length === 1 ? "Here's the change I'll make:" : `Here are the ${changes.length} changes I'll make:`, suggestions: [] };
    }
    return {
        changes,
        reply: "I didn't catch what to change. I can change the branches, the core service and the plan's keywords from here. Try one of these:",
        suggestions: tellSuggestions(draft),
    };
}

/** How long the stand-in takes, so the answer reads as a reply rather than a jump. */
const THINK_MS = 900;

/**
 * Ask Oura. Today the reading runs here; the backend's model replaces it
 * behind the same call (handoff: POST /aeo/scan-drafts/{id}/tell with the
 * text, answering `TellReading` with the same change shapes).
 */
export async function askOura(draft: ScanDraft, text: string): Promise<TellReading> {
    await new Promise((r) => setTimeout(r, THINK_MS));
    return readTell(draft, text);
}
